// src/pages/Admin/ReviewManager.jsx (Updated for Hair Business)
import React, { useState, useEffect, useCallback } from 'react';
import {
    collection,
    getDocs,
    updateDoc,
    doc,
    query,
    orderBy,
    where,
    deleteDoc,
    addDoc
} from 'firebase/firestore';
import { db } from '../services/firebase';
import '../styles/Admin.css';

function ReviewManager({ setMessage }) {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [showAddForm, setShowAddForm] = useState(false);
    const [newReview, setNewReview] = useState({
        customerName: '',
        customerEmail: '',
        rating: 5,
        content: '',
        productType: '',
        hairType: '',
        anonymous: false
    });

    const fetchReviews = useCallback(async () => {
        try {
            setLoading(true);
            const reviewsRef = collection(db, 'reviews');
            let reviewsQuery;

            if (filter === 'all') {
                reviewsQuery = query(reviewsRef, orderBy('createdAt', 'desc'));
            } else {
                reviewsQuery = query(
                    reviewsRef,
                    where('status', '==', filter),
                    orderBy('createdAt', 'desc')
                );
            }

            const snapshot = await getDocs(reviewsQuery);
            const reviewsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setReviews(reviewsData);
        } catch (error) {
            console.error('Error fetching reviews:', error);
            setMessage('Error loading reviews: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [filter, setMessage]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const handleAddReview = async (e) => {
        e.preventDefault();
        try {
            const reviewToSubmit = {
                ...newReview,
                customerName: newReview.anonymous ? 'Anonymous' : newReview.customerName,
                customerEmail: newReview.customerEmail || 'manual@admin.com',
                createdAt: new Date(),
                status: 'approved',
                featured: false,
                source: 'manual'
            };

            await addDoc(collection(db, 'reviews'), reviewToSubmit);

            setMessage('Review added successfully!');
            setShowAddForm(false);
            setNewReview({
                customerName: '',
                customerEmail: '',
                rating: 5,
                content: '',
                productType: '',
                hairType: '',
                anonymous: false
            });
            await fetchReviews();
        } catch (error) {
            console.error('Error adding review:', error);
            setMessage('Error adding review: ' + error.message);
        }
    };

    const updateReviewStatus = async (reviewId, newStatus) => {
        try {
            const reviewRef = doc(db, 'reviews', reviewId);
            await updateDoc(reviewRef, {
                status: newStatus,
                updatedAt: new Date(),
                reviewedBy: 'admin'
            });

            setMessage(`Review ${newStatus} successfully!`);
            await fetchReviews();
        } catch (error) {
            console.error('Error updating review:', error);
            setMessage('Error updating review: ' + error.message);
        }
    };

    const deleteReview = async (reviewId) => {
        if (window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
            try {
                await deleteDoc(doc(db, 'reviews', reviewId));
                setMessage('Review deleted successfully!');
                await fetchReviews();
            } catch (error) {
                console.error('Error deleting review:', error);
                setMessage('Error deleting review: ' + error.message);
            }
        }
    };

    const featureReview = async (reviewId, featured) => {
        try {
            const reviewRef = doc(db, 'reviews', reviewId);
            await updateDoc(reviewRef, {
                featured: featured,
                updatedAt: new Date()
            });

            setMessage(`Review ${featured ? 'featured' : 'unfeatured'} successfully!`);
            await fetchReviews();
        } catch (error) {
            console.error('Error featuring review:', error);
            setMessage('Error updating review: ' + error.message);
        }
    };

    const getStatusCounts = () => {
        const counts = { pending: 0, approved: 0, rejected: 0, total: reviews.length };
        reviews.forEach(review => {
            if (counts.hasOwnProperty(review.status)) {
                counts[review.status]++;
            }
        });
        return counts;
    };

    const statusCounts = getStatusCounts();

    useEffect(() => {
        const badge = document.getElementById('pending-reviews-badge');
        if (badge) {
            badge.textContent = statusCounts.pending;
            badge.style.display = statusCounts.pending > 0 ? 'flex' : 'none';
        }
    }, [statusCounts.pending]);

    if (loading) {
        return <div className="loading">Loading reviews...</div>;
    }

    return (
        <div className="review-manager">
            <div className="review-manager-header">
                <h2>Customer Reviews Management</h2>
                <div className="admin-actions">
                    <button
                        className="btn-add-manual"
                        onClick={() => setShowAddForm(!showAddForm)}
                    >
                        {showAddForm ? 'Cancel' : '+ Add Review'}
                    </button>
                    <div className="status-filters">
                        <button
                            className={filter === 'all' ? 'active' : ''}
                            onClick={() => setFilter('all')}
                        >
                            All Reviews ({statusCounts.total})
                        </button>
                        <button
                            className={filter === 'pending' ? 'active' : ''}
                            onClick={() => setFilter('pending')}
                        >
                            Pending ({statusCounts.pending})
                        </button>
                        <button
                            className={filter === 'approved' ? 'active' : ''}
                            onClick={() => setFilter('approved')}
                        >
                            Approved ({statusCounts.approved})
                        </button>
                        <button
                            className={filter === 'rejected' ? 'active' : ''}
                            onClick={() => setFilter('rejected')}
                        >
                            Rejected ({statusCounts.rejected})
                        </button>
                    </div>
                </div>
            </div>

            {/* Manual Add Form */}
            {showAddForm && (
                <div className="manual-add-form">
                    <h3>Add Customer Review</h3>
                    <form onSubmit={handleAddReview}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Customer Name *</label>
                                <input
                                    type="text"
                                    value={newReview.customerName}
                                    onChange={(e) => setNewReview({...newReview, customerName: e.target.value})}
                                    required
                                    disabled={newReview.anonymous}
                                />
                            </div>
                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={newReview.customerEmail}
                                    onChange={(e) => setNewReview({...newReview, customerEmail: e.target.value})}
                                    placeholder="optional"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Rating *</label>
                                <div className="star-rating-input">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <button
                                            key={star}
                                            type="button"
                                            className={`star ${star <= newReview.rating ? 'active' : ''}`}
                                            onClick={() => setNewReview({...newReview, rating: star})}
                                        >
                                            ★
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Hair Type</label>
                                <select
                                    value={newReview.hairType}
                                    onChange={(e) => setNewReview({...newReview, hairType: e.target.value})}
                                >
                                    <option value="">Select hair type</option>
                                    <option value="straight">Straight</option>
                                    <option value="wavy">Wavy</option>
                                    <option value="curly">Curly</option>
                                    <option value="coily">Coily</option>
                                    <option value="mixed">Mixed</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Product Type</label>
                            <select
                                value={newReview.productType}
                                onChange={(e) => setNewReview({...newReview, productType: e.target.value})}
                            >
                                <option value="">Select product</option>
                                <option value="hair-bundles">Hair Bundles</option>
                                <option value="frontals">Frontals</option>
                                <option value="closures">Closures</option>
                                <option value="hair-care">Hair Care</option>
                                <option value="accessories">Accessories</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Review Content *</label>
                            <textarea
                                value={newReview.content}
                                onChange={(e) => setNewReview({...newReview, content: e.target.value})}
                                required
                                rows="4"
                                placeholder="Enter the customer's review..."
                            />
                        </div>

                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={newReview.anonymous}
                                    onChange={(e) => setNewReview({
                                        ...newReview,
                                        anonymous: e.target.checked
                                    })}
                                />
                                Post as Anonymous
                            </label>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="btn-primary">
                                Add Review
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="reviews-list">
                {reviews.length === 0 ? (
                    <div className="no-reviews">
                        <p>No {filter} reviews found.</p>
                    </div>
                ) : (
                    reviews.map(review => (
                        <div key={review.id} className={`review-item ${review.status}`}>
                            <div className="review-content">
                                <div className="review-meta">
                                    <div className="review-rating">
                                        {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                                        <span className="rating-number">({review.rating}/5)</span>
                                    </div>
                                    <div className="review-author">
                                        <strong>{review.customerName}</strong>
                                        <span>{review.customerEmail}</span>
                                        {review.hairType && <span>• {review.hairType} hair</span>}
                                        {review.source === 'manual' && <span className="manual-badge">Manual</span>}
                                    </div>
                                    <div className="review-product">
                                        {review.productType} • {review.createdAt?.toDate().toLocaleDateString()}
                                    </div>
                                </div>
                                <p className="review-text">"{review.content}"</p>
                            </div>

                            <div className="review-actions">
                                <div className="review-status">
                                    Status: <span className={`status-badge ${review.status}`}>{review.status}</span>
                                    {review.featured && <span className="featured-badge">Featured</span>}
                                </div>

                                <div className="action-buttons">
                                    {review.status === 'pending' && (
                                        <>
                                            <button
                                                className="btn-approve"
                                                onClick={() => updateReviewStatus(review.id, 'approved')}
                                            >
                                                Approve
                                            </button>
                                            <button
                                                className="btn-reject"
                                                onClick={() => updateReviewStatus(review.id, 'rejected')}
                                            >
                                                Reject
                                            </button>
                                        </>
                                    )}

                                    {review.status === 'approved' && (
                                        <>
                                            <button
                                                className={review.featured ? 'btn-unfeature' : 'btn-feature'}
                                                onClick={() => featureReview(review.id, !review.featured)}
                                            >
                                                {review.featured ? 'Unfeature' : 'Feature'}
                                            </button>
                                            <button
                                                className="btn-reject"
                                                onClick={() => updateReviewStatus(review.id, 'rejected')}
                                            >
                                                Reject
                                            </button>
                                        </>
                                    )}

                                    {review.status === 'rejected' && (
                                        <button
                                            className="btn-approve"
                                            onClick={() => updateReviewStatus(review.id, 'approved')}
                                        >
                                            Approve
                                        </button>
                                    )}

                                    <button
                                        className="btn-delete"
                                        onClick={() => deleteReview(review.id)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default ReviewManager;